<template>
    <div class="w-full relative">
        <InputLabel v-if="label" :for="inputId">
            {{ label }}
        </InputLabel>

        <div class="relative">
            <AutoComplete
                ref="autocomplete"
                v-model="model"
                :input-id="inputId"
                :suggestions="filteredOptions"
                option-label="name"
                option-value="value"
                :dropdown="!loading && (props.options?.length ?? 0) > 0 && selectedOption === null"
                :empty-search-message="emptySearchMessage"
                :disabled="disabled"
                :placeholder="placeholder || ''"
                :loading="loading"
                :input-props="{
                    autocomplete: 'off',
                    autocorrect: 'off',
                    name: `search-${inputId}`,
                }"
                v-bind="$attrs"
                :pt="overlayWidthPt"
                @complete="onComplete"
                @option-select="onOptionSelect"
            >
                <template #option="slotProps">
                    <span
                        class="p-autocomplete-option-item"
                        :class="{
                            'p-autocomplete-option-selected': modelValue === slotProps.option.value,
                        }"
                    >
                        {{ slotProps.option.name }}
                    </span>
                </template>
            </AutoComplete>

            <div v-if="selectedOption" class="absolute top-1.5 left-1.5 right-1.5" data-clear>
                <button
                    type="button"
                    class="flex items-center justify-between bg-gray-lightest px-2.5 py-1 text-body rounded-full cursor-pointer w-full custom-focus"
                    :class="
                        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer custom-focus'
                    "
                    :aria-label="__('reset')"
                    :disabled="disabled"
                    @click="clearSelected()"
                >
                    <span
                        aria-hidden="true"
                        class="whitespace-nowrap overflow-hidden overflow-ellipsis pr-1.5"
                    >
                        {{ selectedOption }}
                    </span>
                    <XIcon class="size-4" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import AutoComplete, {
        AutoCompleteCompleteEvent,
        AutoCompleteOptionSelectEvent,
        AutoCompleteProps,
    } from 'primevue/autocomplete';
    import { Option } from './types';
    import { __ } from '@/js/stores/expressions';
    import { computed, onMounted, ref, watch } from 'vue';
    import InputLabel from '../InputLabel.vue';
    import { XIcon } from '@lucide/vue';

    interface Props extends AutoCompleteProps {
        inputId: string;
        options: Option[] | undefined;
        label?: string;
    }

    defineOptions({
        inheritAttrs: false,
    });

    const autocomplete = ref<InstanceType<typeof AutoComplete> | null>(null);

    const model = defineModel<string | Option | null>({ required: true });

    const props = defineProps<Props>();

    const emit = defineEmits<{
        (e: 'select', option: Option): void;
        (e: 'clear'): void;
    }>();

    const selectOption = (option: Option) => {
        emit('select', option);
        model.value = '';

        selectedOption.value = option.name;
    };

    const onOptionSelect = (event: AutoCompleteOptionSelectEvent) => {
        selectOption(event.value as Option);
    };

    const clearSelected = () => {
        if (props.disabled) return;

        emit('clear');
        selectedOption.value = null;
        model.value = null;
        focus();
    };

    const focus = () => {
        (autocomplete.value as any)?.$el?.querySelector('input')?.focus();
    };

    defineExpose({ focus, clearSelected });

    const onOverlayMounted = (vnode: { el: HTMLElement | null }) => {
        const panel = vnode.el;
        const wrapperEl = (autocomplete.value as any)?.$el as HTMLElement | null;
        if (panel && wrapperEl) panel.style.width = `${wrapperEl.offsetWidth}px`;
    };

    const overlayWidthPt = computed(() => ({
        overlay: { onVnodeMounted: onOverlayMounted },
    }));

    const filteredOptions = ref<Option[]>([]);

    const selectedOption = ref<string | null>(null);

    // Auto-select when there is exactly one option and nothing is selected yet.
    const autoSelectSingleOption = (options: Option[] | undefined) => {
        if (options?.length === 1 && selectedOption.value === null) {
            selectOption(options[0]);
        }
    };

    watch(
        () => props.options,
        (newOptions) => {
            filteredOptions.value = newOptions ? [...newOptions] : [];
            autoSelectSingleOption(newOptions);
        },
        { immediate: true }
    );

    onMounted(() => {
        autoSelectSingleOption(props.options);
    });

    watch(
        model,
        (newValue) => {
            if (newValue && typeof newValue === 'object' && 'name' in newValue) {
                selectedOption.value = newValue.name;
                model.value = '';
            } else if (newValue === null) {
                selectedOption.value = null;
            }
        },
        { immediate: true }
    );

    const onComplete = (event: AutoCompleteCompleteEvent) => {
        if (!props.options) {
            filteredOptions.value = [];
            return;
        }

        const query = event.query.toLowerCase().replace(/\s+/g, '');

        filteredOptions.value = query
            ? props.options.filter((x) => x.name.toLowerCase().replace(/\s+/g, '').includes(query))
            : [...props.options];
    };

    const emptySearchMessage = computed(() => {
        if (props.loading) {
            return __('loading');
        }

        if (props.options && props.options.length === 0) {
            return __('no-results-found-change-query');
        }

        return undefined;
    });
</script>
